import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { twilioService } from '../../../lib/twilioService';

const ClassDetailsModal = ({ classData, isOpen, onClose, onBook, onCancel, onJoinWaitlist }) => {
  if (!isOpen || !classData) return null;

  const {
    name,
    instructor,
    instructorImage,
    instructorImageAlt,
    instructorBio,
    instructorRating,
    instructorSpecialties,
    time,
    duration,
    capacity,
    booked,
    difficulty,
    type,
    equipment,
    rating,
    reviews,
    description,
    benefits,
    isBooked,
    isWaitlisted,
    isFull,
    isMemberOnly,
    waitlistPosition
  } = classData;

  const availableSpots = capacity - booked;

  const handleBookClass = async () => {
    if (isBooked) {
      onCancel?.(classData);
    } else if (isFull && !isWaitlisted) {
      onJoinWaitlist?.(classData);
    } else {
      onBook?.(classData);
      
      // Send class reminder SMS (scheduled for 2 hours before class)
      const classDateTime = new Date(`${classData?.date} ${classData?.time}`);
      const reminderTime = new Date(classDateTime?.getTime() - (2 * 60 * 60 * 1000));
      
      if (reminderTime > new Date()) {
        await twilioService?.scheduleSMS(
          null, // Will use user's phone from profile
          `Reminder: Your ${classData?.name} class with ${classData?.instructor} starts at ${classData?.time}. See you there! 🏋️`,
          reminderTime?.toISOString(),
          'class_reminder',
          'class_bookings',
          classData?.id
        );
      }
    }
    onClose?.();
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="w-full max-w-4xl max-h-[90vh] bg-card rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground">
            Class Details
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-muted transition-colors duration-250"
            aria-label="Close modal"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="overflow-y-auto custom-scrollbar" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          <div className="p-6 space-y-6">
            <div className="flex flex-col md:flex-row gap-6">
              <div className="flex-shrink-0">
                <div className="w-full md:w-32 h-48 md:h-32 rounded-lg overflow-hidden">
                  <Image
                    src={instructorImage}
                    alt={instructorImageAlt}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <h3 className="text-2xl font-heading font-semibold text-foreground mb-2">
                      {name}
                    </h3>
                    <div className="flex items-center gap-2 text-muted-foreground mb-2">
                      <Icon name="User" size={18} />
                      <span className="text-base">{instructor}</span>
                    </div>
                  </div>
                  {isMemberOnly && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                      <Icon name="Lock" size={14} className="mr-1" />
                      Members Only
                    </span>
                  )}
                </div>

                <div className="flex flex-wrap gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={18} color="var(--color-primary)" />
                    <span className="text-sm">{time}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Timer" size={18} color="var(--color-primary)" />
                    <span className="text-sm">{duration} minutes</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Users" size={18} color="var(--color-primary)" />
                    <span className="text-sm">{booked}/{capacity} booked</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon name="Star" size={18} color="var(--color-warning)" />
                    <span className="text-sm">{rating} ({reviews} reviews)</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-accent/10 text-accent text-sm font-medium">
                    {type}
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                    {difficulty}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-3">
                About This Class
              </h4>
              <p className="text-muted-foreground leading-relaxed">
                {description}
              </p>
            </div>

            <div className="border-t border-border pt-6">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-3">
                Benefits
              </h4>
              <ul className="space-y-2">
                {benefits?.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <Icon name="Check" size={18} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border-t border-border pt-6">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-3">
                Equipment Needed
              </h4>
              <div className="flex flex-wrap gap-2">
                {equipment?.map((item, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-2 rounded-lg bg-muted text-foreground text-sm"
                  >
                    <Icon name="Dumbbell" size={16} className="mr-2" />
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <h4 className="text-lg font-heading font-semibold text-foreground mb-3">
                About the Instructor
              </h4>
              <div className="flex items-center gap-2 mb-3">
                <Icon name="Star" size={18} color="var(--color-warning)" />
                <span className="text-sm font-medium">{instructorRating} Instructor Rating</span>
              </div>
              <p className="text-muted-foreground leading-relaxed mb-4">
                {instructorBio}
              </p>
              <div className="flex flex-wrap gap-2">
                {instructorSpecialties?.map((specialty, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm"
                  >
                    {specialty}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 p-6 border-t border-border bg-muted/30">
          {isBooked ? (
            <>
              <Button
                variant="outline"
                className="flex-1"
                iconName="Calendar"
                iconPosition="left"
              >
                Already Booked
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  onCancel(classData?.id);
                  onClose();
                }}
                iconName="X"
                iconPosition="left"
              >
                Cancel Booking
              </Button>
            </>
          ) : isWaitlisted ? (
            <Button
              variant="outline"
              fullWidth
              iconName="Clock"
              iconPosition="left"
            >
              On Waitlist (Position #{waitlistPosition})
            </Button>
          ) : isFull ? (
            <Button
              variant="secondary"
              fullWidth
              onClick={() => {
                onJoinWaitlist(classData?.id);
                onClose();
              }}
              iconName="UserPlus"
              iconPosition="left"
            >
              Join Waitlist
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                onClick={onClose}
              >
                Close
              </Button>
              <Button
                variant="default"
                className="flex-1"
                onClick={handleBookClass}
                iconName="Calendar"
                iconPosition="left"
              >
                Book This Class ({availableSpots} spots left)
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClassDetailsModal;